import type { FinancialReconciliationKind } from "@/api/financeiro/reconciliation";
import { djs } from "@/lib/djs";
import { OPERATION_ZONE } from "@/lib/labels/shared";

export const finTimelineLabel: Record<"payment_received" | "payment_reversed", string> = {
	payment_received: "Pagamento recebido",
	payment_reversed: "Pagamento estornado",
};

export const finBrl = new Intl.NumberFormat("pt-BR", {
	style: "currency",
	currency: "BRL",
});

const receiptMethod = {
	pix: "PIX",
	transfer: "Transferência",
	cash: "Espécie",
	debit_card: "Cartão de débito",
	credit_card: "Cartão de crédito",
	other: "Outro cartão",
} as const;

const accountSubtype: Record<string, string> = {
	CHECKING_ACCOUNT: "Conta corrente",
	SAVINGS_ACCOUNT: "Poupança",
};

const receiptState = {
	confirmed: "confirmado",
	pending: "falta comprovante",
	reversed: "estornado",
	cancelled: "cancelado",
} as const;

const reconciliationKind: Record<FinancialReconciliationKind, string> = {
	receipt_bank_missing: "falta confirmar a entrada no banco",
	receipt_card_missing: "falta confirmar a venda no cartão",
	receipt_evidence_lost: "o comprovante deixou de ser válido",
	bill_bank_missing: "falta confirmar a saída no banco",
	bill_evidence_lost: "o comprovante deixou de ser válido",
	bank_income_unmatched: "entrada bancária sem recebimento",
	bank_expense_unmatched: "saída bancária sem conta",
	cash_deposit_missing: "depósito saiu do caixa e não apareceu no banco",
	cash_deposit_evidence_lost: "a entrada vinculada deixou de ser válida",
	card_sale_unmatched: "venda no cartão sem paciente",
	card_settlement_components_missing: "detalhamento do repasse ainda não foi importado",
	card_settlement_divergent: "valor do repasse difere das ordens de crédito",
	card_settlement_sales_incomplete: "ordem de crédito com vendas faltando ou excedentes",
	card_settlement_sales_chargeback: "repasse contém venda com chargeback",
	card_settlement_sales_reversed: "repasse contém venda cancelada",
	card_settlement_sales_unmatched: "repasse contém vendas sem paciente",
	card_settlement_unmatched: "depósito dos cartões sem entrada bancária",
	card_settlement_evidence_lost: "a entrada vinculada deixou de ser válida",
};

const reconciliationSubject: Record<FinancialReconciliationKind, string> = {
	receipt_bank_missing: "Recebimento sem comprovante",
	receipt_card_missing: "Recebimento sem comprovante",
	receipt_evidence_lost: "Recebimento precisa de novo comprovante",
	bill_bank_missing: "Conta paga sem comprovante",
	bill_evidence_lost: "Conta precisa de novo comprovante",
	bank_income_unmatched: "Entrada bancária",
	bank_expense_unmatched: "Saída bancária",
	cash_deposit_missing: "Depósito sem entrada bancária",
	cash_deposit_evidence_lost: "Depósito precisa de novo comprovante",
	card_sale_unmatched: "Venda no cartão",
	card_settlement_components_missing: "Repasse sem detalhamento",
	card_settlement_divergent: "Repasse com diferença",
	card_settlement_sales_incomplete: "Repasse com vendas incompletas",
	card_settlement_sales_chargeback: "Repasse com chargeback",
	card_settlement_sales_reversed: "Repasse com venda cancelada",
	card_settlement_sales_unmatched: "Repasse com vendas não identificadas",
	card_settlement_unmatched: "Depósito dos cartões",
	card_settlement_evidence_lost: "Depósito precisa de novo comprovante",
};

const reconciliationAction: Record<FinancialReconciliationKind, string> = {
	receipt_bank_missing: "Vincular",
	receipt_card_missing: "Vincular",
	receipt_evidence_lost: "Trocar",
	bill_bank_missing: "Vincular",
	bill_evidence_lost: "Trocar",
	bank_income_unmatched: "Registrar",
	bank_expense_unmatched: "Criar conta",
	cash_deposit_missing: "Vincular",
	cash_deposit_evidence_lost: "Trocar",
	card_sale_unmatched: "Registrar recebimento",
	card_settlement_components_missing: "Abrir repasse",
	card_settlement_divergent: "Abrir repasse",
	card_settlement_sales_incomplete: "Abrir repasse",
	card_settlement_sales_chargeback: "Abrir repasse",
	card_settlement_sales_reversed: "Abrir repasse",
	card_settlement_sales_unmatched: "Abrir repasse",
	card_settlement_unmatched: "Confirmar depósito",
	card_settlement_evidence_lost: "Trocar",
};

function monthDay(month: string) {
	return djs.tz(`${month}-01T00:00:00`, OPERATION_ZONE);
}

function capitalize(value: string) {
	return value.charAt(0).toUpperCase() + value.slice(1);
}

export const FinanceiroLabels = {
	loadError: "Não foi possível carregar o financeiro.",
	receipt: { method: receiptMethod, state: receiptState },
	reconciliation: {
		kind: reconciliationKind,
		subject: reconciliationSubject,
		action: reconciliationAction,
	},
	money(amountCents: number) {
		return finBrl.format(amountCents / 100);
	},
	accountDetail(account: { subtype: string | null; institution_number: string | null }) {
		const parts = [
			account.subtype === null ? null : (accountSubtype[account.subtype] ?? account.subtype),
			account.institution_number,
		];

		return parts.filter((part) => part !== null).join(" - ");
	},
	monthLongYear(month: string) {
		return monthDay(month).locale("pt-br").format("MMMM YYYY");
	},
	monthTitle(month: string) {
		return capitalize(monthDay(month).locale("pt-br").format("MMMM"));
	},
	dayLong(day: string) {
		return capitalize(
			djs
				.tz(`${day}T00:00:00`, OPERATION_ZONE)
				.locale("pt-br")
				.format("dddd, D [de] MMMM [de] YYYY"),
		);
	},
	signed(amountCents: number) {
		const value = finBrl.format(Math.abs(amountCents) / 100);

		return amountCents < 0 ? `-${value}` : `+${value}`;
	},
};
